import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { TEMP } from "@zapier/canvas-constants";
import { isStreamingZslAtom } from "../../../streaming/src/atoms/streamingStatusAtoms";
import { canvasAtom } from "../canvas";
import { deriveZslFromCanvasAtom } from "../utils";
import { zslAtom } from "../templates";
import {
  abortControllerAtomFamily,
  abortCurrentStreamSetterAtomFamily,
} from "../canvasAi";

import { allZapStepEdgesAtomFamily } from "../zaps";
import { areHandlesVisibleAtomFamily } from "../handles";
import { zapStepNodesVisibleAtomFamily } from "../zaps";
import { childrenEdgesAtomFamily } from "../edges";
import { hasSourceNodeAtomFamily } from "../edges";
import { hasVisibleAnnotationAtomFamily } from "../tour";
import { lastBranchingPathPositionAtomFamily } from "../paths";
import {
  aiSuggestedEditsAtomFamily,
  aiSuggestedEditsLoadingAtomFamily,
  aiSuggestionErrorAtomFamily,
  isZslExecutionLoadingAtomFamily,
  zslExecutionErrorAtomFamily,
} from "./aiSuggestedEditsAtomFamily";
import { assetTypeAndLinkAtomFamily } from "./assetTypeAndLinkAtomFamily";
import { isEditingTextNodeAtomFamily } from "./isEditingTextNodeAtomFamily";
import { isMultiSelectedAtomFamily } from "./isMultiSelectedAtomFamily";
import { isNodeHoveredAtomFamily } from "./isNodeHoveredAtomFamily";
import { isOnlyNodeSelectedAtomFamily } from "./isOnlyNodeSelectedAtomFamily";
import { nodeAtomFamily } from "./nodeAtomFamily";
import { nodeClassNameAtomFamily } from "./nodeClassNameAtomFamily";
import { nodeLabelAtomFamily } from "./nodeLabelAtomFamily";
import {
  suggestedNodeAtomFamily,
  suggestedNodeHeightsAtomFamily,
} from "./aiSuggestedEditsAtomFamily";
import { isDraftModeExcludingTemplatesAtom } from "./isDraftModeExcludingTemplatesAtom";

// Create a base atom for the nodes state
const baseNodesAtom = atom<Node[]>([]);
baseNodesAtom.debugLabel = "baseNodesAtom";

export const nodesAtom = atom<
  Node[],
  [Node[] | ((prev: Node[]) => Node[])],
  void
>(
  // Getter
  (get) => get(baseNodesAtom),
  // Setter
  (get, set, update: Node[] | ((prev: Node[]) => Node[])) => {
    // Get current nodes state
    const currentNodes = get(baseNodesAtom);
    const isStreamingZsl = get(isStreamingZslAtom);
    // Apply the update (handling both direct and functional updates)
    const newNodes =
      typeof update === "function" ? update(currentNodes) : update;

    // Find removed node IDs
    const removedNodeIds = currentNodes
      .filter((n) => !newNodes.some((nn) => nn.id === n.id))
      .map((n) => n.id);

    // Clean up atom families for removed nodes to prevent memory leaks
    removedNodeIds.forEach((nodeId) => {
      if (nodeId === TEMP) return; // Skip cleanup since no atomFamily is associated with it

      abortControllerAtomFamily.remove(nodeId);
      abortCurrentStreamSetterAtomFamily.remove(nodeId);
      aiSuggestedEditsAtomFamily.remove(nodeId);
      aiSuggestedEditsLoadingAtomFamily.remove(nodeId);
      aiSuggestionErrorAtomFamily.remove(nodeId);
      allZapStepEdgesAtomFamily.remove(nodeId);
      areHandlesVisibleAtomFamily.remove(nodeId);
      assetTypeAndLinkAtomFamily.remove(nodeId);
      childrenEdgesAtomFamily.remove(nodeId);
      hasSourceNodeAtomFamily.remove(nodeId);
      hasVisibleAnnotationAtomFamily.remove(nodeId);
      isEditingTextNodeAtomFamily.remove(nodeId);
      isMultiSelectedAtomFamily.remove(nodeId);
      isNodeHoveredAtomFamily.remove(nodeId);
      isOnlyNodeSelectedAtomFamily.remove(nodeId);
      isZslExecutionLoadingAtomFamily.remove(nodeId);
      lastBranchingPathPositionAtomFamily.remove(nodeId);
      nodeAtomFamily.remove(nodeId);
      nodeClassNameAtomFamily.remove(nodeId);
      nodeLabelAtomFamily.remove(nodeId);
      suggestedNodeAtomFamily.remove(nodeId);
      suggestedNodeHeightsAtomFamily.remove(nodeId);
      zapStepNodesVisibleAtomFamily.remove(nodeId);
      zslExecutionErrorAtomFamily.remove(nodeId);
    });

    // Update nodes state
    set(baseNodesAtom, newNodes);

    // Get current canvas
    const canvas = get(canvasAtom);
    const isDraftModeNotTemplates = get(isDraftModeExcludingTemplatesAtom);

    // Update ZSL state using the utility function
    if (isDraftModeNotTemplates && !isStreamingZsl) {
      const currentZsl = get(zslAtom);
      if (currentZsl) {
        const deriveZslFromCanvas = get(deriveZslFromCanvasAtom);
        const updatedZsl = deriveZslFromCanvas(currentZsl, canvas, newNodes);
        if (updatedZsl) {
          set(zslAtom, updatedZsl);
        }
      }
    }
  },
);
nodesAtom.debugLabel = "nodesAtom";
